import { message } from "antd";
import React, { useEffect, useState } from "react";
import uuid from "uuid";

const TrackingController = ({
  children,
  dataMultiOrgTags,
  newDataMultiOrgTags,
  tagTrackingResetAmplify,
  editMultiOrgTag,
  getNewMultiOrgTags,
  deleteMultiOrgTag,
  createMultiOrg,
  getBasicTagActivity,
  status,
  updateActiveEventsTarget,
  ...props
}) => {
  const [multipleTags, setMultipleTags] = useState(dataMultiOrgTags);

  const setOrgTagConfigAmplifyDefault = async record => {
    //Resetting all tags to false.
    //This resets all tag isAmplifyDefault that is connected to this org. This means that if the tag is connected to another org, it will also be reset to false.
    await tagTrackingResetAmplify({
      variables: {
        isAmplifyDefault: false,
      },
    });

    await editMultiOrgTag({
      variables: {
        eventId: record.eventId,
        tagId: record.tagId,
        appId: record.appId,
        isAmplifyDefault: true,
      },
    }).then(async () => {
      await getNewMultiOrgTags();
    });
  };

  const onDeleteMultiOrgTag = async (id, hasReports) => {
    if (hasReports) {
      await updateActiveEventsTarget({
        variables: {
          id,
          active: false,
        },
      }).then(async () => {
        await getNewMultiOrgTags();
      });
    } else {
      await deleteMultiOrgTag({
        variables: {
          eventId: id,
        },
      }).then(async () => {
        await getNewMultiOrgTags();
      });
    }

    await message.success(`Successfully deleted tag`);
  };

  const getEnvironment = cart => {
    let environment = "";
    switch (cart) {
      case "":
      case "NOCART":
      case "THIRDPARTY":
        break;
      case "DUTCHIEJS":
        environment = `&environment=dutchie-iframe`;
        break;
      case "DUTCHIE":
        environment = `&environment=dutchie-subdomain`;
        break;
      case "SHOPIFYUNIVERSAL":
        environment = `&environment=shopify`;
        break;
      default:
        environment = `&environment=${cart.toLowerCase()}`;
        break;
    }
    return environment;
  };

  const newShopify = shopifySnippet => {
    const shopifyCode = `
    var tag = window.document.createElement("script");
    tag.src = ${shopifySnippet}
    document.head.appendChild(tag);

    analytics.subscribe("checkout_completed", (event) => {
      try {
        const products = event.data.checkout;
        const lineItems = products.lineItems;
        window.tracker(
          "addTrans",
          products.order.id,
          "N/A",
          parseFloat(products.totalPrice.amount),
          parseFloat(products.totalTax.amount) || 0,
          parseFloat(products.shippingLine.price.amount) || 0,
          "N/A",
          "N/A",
          "N/A",
          products.currencyCode || "USD"
        );
        lineItems.forEach(function (item) {
          const productItem = item.variant;

          window.tracker(
            "addItem",
            item.id,
            productItem.sku || "N/A",
            productItem.product.title || "N/A",
            "N/A",
            parseFloat(productItem.price.amount) || 0,
            parseInt(item.quantity) || 1,
            products.currencyCode || "USD"
          );
        });
        window.tracker("trackTrans");
      } catch (error) {}
    });
    `;

    return { shopifyCode };
  };

  const onCreateMultiTags = async (cart, name, tagUpdateDate) => {
    const customSnippets = snippet => {
      const woocommerce = `function woocommerce_tracker() { ?>
    
    <!-- Header scripts go here -->
      ${snippet}
    <!-- Header scripts go here -->    
    
    <?php
      $product = array();
        global $wp_query;
        if( !empty( $wp_query->query_vars[ 'order-received' ] ) ) {
        $order = wc_get_order( $wp_query->query_vars[ 'order-received' ] );
        $items = $order->get_items();
        if(!empty($items)) {
          foreach($items as $item) {
            $product[] = $item->get_data();
          }
        }
    
        $check = get_post_meta( $order->get_id(), 'thank_you_page_check', true );
        if ( $check ) {
          return;
        }
        update_post_meta( $order->get_id(), 'thank_you_page_check', true );
    ?>
        <script> 
          var transactionOrder = <?php echo $order; ?>;
          var transactionItems = <?php echo json_encode($product); ?>;
        </script>
    <?php }
    }
    add_action( 'wp_head', 'woocommerce_tracker' );`;

      return { woocommerce };
    };

    const getUID = uuid.v4();
    const shopifySnippet = `"https://tags.cnna.io/?appId=${getUID}";`;

    const evaluateSnippet = ({ snippet }) => {
      if (cart === "WOOCOMMERCE") return customSnippets(snippet).woocommerce;
      if (cart === "SHOPIFYUNIVERSAL")
        return newShopify(shopifySnippet).shopifyCode;

      return snippet;
    };

    const environment = getEnvironment(cart);
    const baseUrl = `https://tags.cnna.io/?appId=${getUID}${environment}`;
    const snippet = `<script type="text/javascript" src="${baseUrl}"></script>`;
    const evalutedSnippet = evaluateSnippet({ snippet: snippet });

    const getCart = (() => {
      switch (cart) {
        case "dutchie-iframe":
          return "DUTCHIEJS";
        case "dutchie-subdomain":
          return "DUTCHIE";
        case "shopifyuniversal":
          return "SHOPIFY";
        default:
          return cart;
      }
    })();

    await createMultiOrg({
      variables: {
        cart: getCart,
        appId: getUID,
        name: name,
        tagUpdateDate: tagUpdateDate,
        tag: { generatedTag: evalutedSnippet },
      },
    }).then(val => {
      const getVal =
        val &&
        val.data &&
        val.data.updateOrgMultiTag &&
        val.data.updateOrgMultiTag.eventsTarget;

      setMultipleTags(getVal);
    });
  };

  const onEditMultiTags = async (
    tagId,
    cart,
    name,
    appId,
    tagUpdateDate,
    isAmplifyDefault,
    segmentId,
    eventId
  ) => {
    const customSnippets = snippet => {
      const woocommerce = `function woocommerce_tracker() { ?>
    
    <!-- Header scripts go here -->
      ${snippet}
    <!-- Header scripts go here -->    
    
    <?php
      $product = array();
        global $wp_query;
        if( !empty( $wp_query->query_vars[ 'order-received' ] ) ) {
        $order = wc_get_order( $wp_query->query_vars[ 'order-received' ] );
        $items = $order->get_items();
        if(!empty($items)) {
          foreach($items as $item) {
            $product[] = $item->get_data();
          }
        }
    
        $check = get_post_meta( $order->get_id(), 'thank_you_page_check', true );
        if ( $check ) {
          return;
        }
        update_post_meta( $order->get_id(), 'thank_you_page_check', true );
    ?>
        <script> 
          var transactionOrder = <?php echo $order; ?>;
          var transactionItems = <?php echo json_encode($product); ?>;
        </script>
    <?php }
    }
    add_action( 'wp_head', 'woocommerce_tracker' );`;

      return { woocommerce };
    };

    const shopifySnippet = `"https://tags.cnna.io/?segmentId=${segmentId}&appId=${appId}";`;

    const evaluateSnippet = ({ snippet }) => {
      if (cart === "WOOCOMMERCE") return customSnippets(snippet).woocommerce;
      if (cart === "SHOPIFYUNIVERSAL")
        return newShopify(shopifySnippet).shopifyCode;
      return snippet;
    };

    const environment = getEnvironment(cart);
    const baseUrl = `https://tags.cnna.io/?segmentId=${segmentId}&appId=${appId}${environment}`;
    const snippet = `<script type="text/javascript" src="${baseUrl}"></script>`;
    const evalutedSnippet = evaluateSnippet({ snippet: snippet });

    switch (cart) {
      case "dutchie-iframe":
        cart = "DUTCHIEJS";
        break;
      case "dutchie-subdomain":
        cart = "DUTCHIE";
        break;
      default:
        break;
    }

    await editMultiOrgTag({
      variables: {
        tagId: tagId,
        cart: cart,
        name: name,
        appId: appId,
        tagUpdateDate: tagUpdateDate,
        isAmplifyDefault: isAmplifyDefault,
        tag: { generatedTag: evalutedSnippet },
        eventId: eventId ? eventId : "",
      },
    }).then(val => {
      const getVal =
        val &&
        val.data &&
        val.data.updateOrg &&
        val.data.updateOrg.eventsTarget;

      setMultipleTags(getVal);
    });
    await message.success(`Successfully updated tag`);
  };

  useEffect(() => {
    if (newDataMultiOrgTags) {
      setMultipleTags(newDataMultiOrgTags.org.eventsTarget);
    }
  }, [newDataMultiOrgTags]);

  return React.cloneElement(children, {
    multipleTags,
    onEditMultiTags,
    setOrgTagConfigAmplifyDefault,
    onDeleteMultiOrgTag,
    onCreateMultiTags,
    ...props,
  });
};

export default TrackingController;
